/**
 * @module services/sDialog/component
 */

import singletons from "@/services/singletons";
import {
  ISDialogOptionsInternal,
  ISDialogOptions
} from "@/services/sDialog/classes/interfaces";

import _ from "lodash";

/**
 * A Dialog reference exposing options to control opened dialog.
 *
 * @class SDialogRef
 */
class SDialogRef {
  /**
   * ID of current dialog.
   *
   * @type {string}
   * @memberof SDialogRef
   */
  id: string;
  private closeResolve: (
    value?: any | PromiseLike<any> | undefined
  ) => void = () => {};
  private currentDialogOptions: ISDialogOptionsInternal;
  /**
   * Promise which will be resolved (with data passed to SDialogRef.close) when dialog is closed.
   *
   * @type {Promise<any>}
   * @memberof SDialogRef
   */
  afterClose: Promise<any>;

  /**
   * Creates an instance of SDialogRef. Injects DialogRef to component.
   * @param {string} id Unique ID of dialog.
   * @memberof SDialogRef
   */
  constructor(id: string, dialogOptions: ISDialogOptionsInternal) {
    this.id = id;
    dialogOptions.dialogRef = this;
    this.currentDialogOptions = dialogOptions;

    singletons.sDialog.$nextTick(() => {
      this.currentDialogOptions.active = true;
      this.setDialogRef();
    });

    this.afterClose = new Promise(resolve => {
      this.closeResolve = resolve;
    });
  }

  /**
   * Method to update currently opened sDialog.
   *
   * @param {ISDialogOptions} sDialogOptions Dialog option to update.
   * @memberof SDialogRef
   */
  update(sDialogOptions: ISDialogOptions) {
    _.each(sDialogOptions, (value, key) => {
      this.currentDialogOptions[key] = value;
    });
    if (singletons.sDialog.$vuetify.breakpoint.smAndDown)
      this.currentDialogOptions.maxWidth = "95%";

    // this.currentDialogOptions.keepAlive = _.propOrDefault(
    //   sDialogOptions,
    //   "keepAlive",
    //   false
    // );

    if (sDialogOptions.component) {
      this.setDialogRef();
    }
  }

  private setDialogRef() {
    singletons.sDialog.$nextTick(() => {
      singletons.sDialog.$refs[
        this.currentDialogOptions.compId
      ][0].$sDialogRef = this;
    });
  }

  /**
   * Method to close current dialog.
   *
   * @param {*} [result] Data to pass to afterClose Promise callbacks.
   * @memberof SDialogRef
   */
  close(result?: any) {
    this.currentDialogOptions.active = false;
    singletons.sDialog.$nextTick(() => {
      singletons.sDialog.dialogs = _.pullAllBy(
        singletons.sDialog.dialogs,
        [{ id: this.id }],
        "id"
      ) as ISDialogOptionsInternal[];

      this.closeResolve(result);
    });
  }
}

export default SDialogRef;
